import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { TableVirtuoso, TableVirtuosoHandle } from 'react-virtuoso';

import { ColumnFilterDropdown } from './filter-components';
import {
  ActionsCell,
  ActionsHeaderCell,
  CellContent,
  DataCell,
  DataTableHeader,
  EmptyState,
  HeaderCell,
  HeaderRow,
  LoadingOverlay,
  ResizeHandle,
  SelectionCell,
  SelectionHeaderCell,
  SortButton,
  SortIcon,
  StyledTable,
  TableBody,
  TableContainer,
  TableHeader,
  TableWrapper,
} from './styles';
import {
  ColumnConfig,
  ColumnFilter,
  DataTableProps,
  DataTableStoredConfig,
  FilterState,
  SortDirection,
  SortState,
  TableRowData,
} from './types';

import { Checkbox, Dropdown, Icon, Tooltip } from '@controlrooms/components';
import { ICONS } from '@controlrooms/constants';

// Virtuoso components
const VirtuosoScroller = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  (props, ref) => <TableWrapper ref={ref} {...props} />,
);

const VirtuosoTable = (
  props: React.HTMLAttributes<HTMLTableElement> & { style?: React.CSSProperties },
) => <StyledTable {...props} />;

const VirtuosoTableHead = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>((props, ref) => <TableHeader {...props} ref={ref} />);

const VirtuosoTableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>((props, ref) => <TableBody {...props} ref={ref} />);

const ColumnPicker: React.FC<{
  columns: ColumnConfig[];
  visibleColumns: string[];
  onToggleColumn: (columnKey: string) => void;
  buttonRef: React.RefObject<HTMLButtonElement>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ columns, visibleColumns, onToggleColumn, buttonRef, open, onOpenChange }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Position the menu below the button
  useEffect(() => {
    if (open && buttonRef.current && menuRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      menuRef.current.style.position = 'absolute';
      menuRef.current.style.top = `${rect.bottom + window.scrollY}px`;
      menuRef.current.style.left = `${rect.left + window.scrollX + 20}px`;
    }
  }, [open, buttonRef]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        onOpenChange(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, buttonRef, onOpenChange]);

  return (
    <>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          onOpenChange(!open);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          background: 'none',
          border: 'none',
          color: '#737373',
          cursor: 'pointer',
        }}
      >
        <Icon name={ICONS.Kebab} width="16" height="16" />
      </button>
      {open &&
        ReactDOM.createPortal(
          <div ref={menuRef} style={{ zIndex: 1000 }}>
            <Dropdown.Options position="left" open={open}>
              <Dropdown.Title>Columns</Dropdown.Title>
              {columns.map((column) => (
                <Dropdown.Item
                  key={column.key}
                  onClick={() => onToggleColumn(column.key)}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '150px' }}
                >
                  <Checkbox
                    checked={visibleColumns.includes(column.key)}
                    onChange={() => onToggleColumn(column.key)}
                  />
                  {column.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Options>
          </div>,
          document.body,
        )}
    </>
  );
};

const ROW_SINGLE_CLICK_DELAY_MS = 300;

const DataTable = <T extends Record<string, any>>({
  id,
  data,
  columns,
  multiSelect = false,
  selectedRowIds,
  onRowClick,
  onRowDoubleClick,
  onRowSelect,
  renderActions,
  title,
  isDataLoading = false,
  config,
  onColumnResize,
  onColumnVisibilityChange,
  onSortChange,
  onColumnOrderChange,
  onFilterChange,
  selectedConfigFilter,
  selectedConfigSort,
}: DataTableProps<T>) => {
  const {
    sessionPersistence,
    rowHeight,
    className,
    style,
    borderedCell,
    highlightSelectedRow,
    showColumnPicker,
    showActionCell,
  } = config?.tableConfig || {};
  const { timezone } = config?.dataConfig || {};

  const rowSingleClickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRowClickRef = useRef<{ rowId: string | number; time: number } | null>(null);

  useEffect(() => {
    return () => {
      if (rowSingleClickTimeoutRef.current !== null) {
        clearTimeout(rowSingleClickTimeoutRef.current);
        rowSingleClickTimeoutRef.current = null;
      }
    };
  }, []);

  // Single session storage key for all table config
  const storageKey = useMemo(() => `dataTable-${id}`, [id]);

  // Parse stored config once for all initializers
  const storedConfig = useMemo(() => {
    if (!sessionPersistence) return null;
    const stored = sessionStorage.getItem(storageKey);
    if (stored) {
      try {
        return JSON.parse(stored) as DataTableStoredConfig;
      } catch {
        return null;
      }
    }
    return null;
  }, [sessionPersistence, storageKey]);

  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    if (storedConfig?.visibleColumns) {
      return storedConfig.visibleColumns;
    }
    return columns.filter((col) => col.visible !== false).map((col) => col.key);
  });

  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    if (storedConfig?.columnOrder) {
      const validOrder = storedConfig.columnOrder.filter((key: string) =>
        columns.some((col) => col.key === key),
      );
      if (validOrder.length > 0) {
        return validOrder;
      }
    }
    return columns.filter((col) => col.visible !== false).map((col) => col.key);
  });

  // Drag and drop state
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [dropPosition, setDropPosition] = useState<'left' | 'right' | null>(null);

  // Memoize initial column widths calculation
  const initialColumnWidths = useMemo(() => {
    const initialWidths: Record<string, number> = {};
    columns.forEach((col) => {
      initialWidths[col.key] = col.width || 180;
    });

    if (storedConfig?.columnWidths) {
      const widths = storedConfig.columnWidths;
      // Merge with defaults for any new columns
      Object.keys(initialWidths).forEach((key) => {
        if (widths[key]) {
          initialWidths[key] = widths[key];
        }
      });
    }

    return initialWidths;
  }, [columns, storedConfig]);

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(initialColumnWidths);

  const [sortState, setSortState] = useState<SortState>(() => {
    if (storedConfig?.sort) {
      return storedConfig.sort;
    }

    if (!sessionPersistence && selectedConfigSort) {
      return selectedConfigSort;
    }

    return {
      columnKey: null,
      direction: 'none',
    };
  });

  // Sync sort when selectedConfigSort prop changes (when sessionPersistence is disabled)
  useEffect(() => {
    if (!sessionPersistence && selectedConfigSort !== undefined) {
      setSortState(selectedConfigSort);
    }
  }, [selectedConfigSort, sessionPersistence]);

  const [isResizing, setIsResizing] = useState<{
    columnKey: string;
    startX: number;
    startWidth: number;
  } | null>(null);

  // Track if state change is from internal user action to prevent sync loops
  const isInternalChangeRef = useRef(false);

  // Single useEffect to sync columns when they change from props
  useEffect(() => {
    // Skip sync if:
    // 1. Session persistence is enabled (state managed via sessionStorage)
    // 2. Change originated from internal user action (resize/visibility toggle/drag)
    if (sessionPersistence || isInternalChangeRef.current) {
      isInternalChangeRef.current = false; // Reset flag for next render
      return;
    }

    // Sync external prop changes to internal state
    const newVisibleColumns = columns.filter((col) => col.visible !== false).map((col) => col.key);

    const newWidths: Record<string, number> = {};
    columns.forEach((col) => {
      newWidths[col.key] = col.width || 180;
    });

    setColumnOrder(newVisibleColumns);

    setVisibleColumns(newVisibleColumns);
    setColumnWidths(newWidths);
  }, [columns, sessionPersistence]);

  const [filters, setFilters] = useState<FilterState>(() => {
    const defaultFilters: FilterState = {};
    columns.forEach((col) => {
      if (col.defaultFilter) {
        defaultFilters[col.key] = col.defaultFilter;
      }
    });

    if (storedConfig?.filters) {
      return { ...defaultFilters, ...storedConfig.filters };
    }

    // Use selectedConfigFilter prop only when sessionPersistence is disabled
    if (!sessionPersistence && selectedConfigFilter !== undefined) {
      return { ...defaultFilters, ...selectedConfigFilter };
    }

    return defaultFilters;
  });

  // Sync filters when selectedConfigFilter prop changes (when sessionPersistence is disabled)
  useEffect(() => {
    if (!sessionPersistence) {
      const defaultFilters: FilterState = {};
      columns.forEach((col) => {
        if (col.defaultFilter) {
          defaultFilters[col.key] = col.defaultFilter;
        }
      });
      setFilters(
        selectedConfigFilter !== undefined
          ? { ...defaultFilters, ...selectedConfigFilter }
          : defaultFilters,
      );
    }
  }, [selectedConfigFilter, sessionPersistence, columns]);

  // Internal selection state - controlled by selectedRowIds prop if provided
  const [internalSelectedRows, setInternalSelectedRows] = useState<Set<string | number>>(new Set());

  // Use controlled selection if provided, otherwise use internal state
  const selectedRows = useMemo(() => {
    if (selectedRowIds !== undefined) {
      return new Set(selectedRowIds);
    }
    return internalSelectedRows;
  }, [selectedRowIds, internalSelectedRows]);

  // Sync internal state when controlled selection changes
  useEffect(() => {
    if (selectedRowIds !== undefined) {
      setInternalSelectedRows(new Set(selectedRowIds));
    }
  }, [selectedRowIds]);

  const [highlightedRowId, setHighlightedRowId] = useState<string | number | null>(null);
  const [isKeyboardNavEnabled, setIsKeyboardNavEnabled] = useState(false);

  const columnPickerRef = useRef<HTMLButtonElement>(null);
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const filterRefs = useRef<Record<string, React.RefObject<HTMLDivElement>>>({});
  const virtuosoRef = useRef<TableVirtuosoHandle>(null);
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Persist all table state as a single config object
  useEffect(() => {
    if (!sessionPersistence) return;

    sessionStorage.setItem(
      storageKey,
      JSON.stringify({
        visibleColumns,
        columnWidths,
        filters,
        columnOrder,
        sort: sortState,
      }),
    );
  }, [
    visibleColumns,
    columnWidths,
    filters,
    columnOrder,
    sortState,
    sessionPersistence,
    storageKey,
  ]);

  // Handle column resize
  const handleResizeStart = useCallback(
    (columnKey: string, startX: number) => {
      setIsResizing({
        columnKey,
        startX,
        startWidth: columnWidths[columnKey],
      });
    },
    [columnWidths],
  );

  const handleResizeMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaX = e.clientX - isResizing.startX;
      const newWidth = Math.max(100, isResizing.startWidth + deltaX); // Minimum 100px

      // Mark as internal change to prevent sync override
      isInternalChangeRef.current = true;

      setColumnWidths((prev) => ({
        ...prev,
        [isResizing.columnKey]: newWidth,
      }));

      // Emit column resize event
      onColumnResize?.(isResizing.columnKey, newWidth);
    },
    [isResizing, onColumnResize],
  );

  const handleResizeEnd = useCallback(() => {
    setIsResizing(null);
  }, []);

  // Add mouse event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Handle column visibility toggle
  const handleToggleColumn = useCallback(
    (columnKey: string) => {
      // Mark as internal change to prevent sync override
      isInternalChangeRef.current = true;

      setVisibleColumns((prev) => {
        const isCurrentlyVisible = prev.includes(columnKey);
        const newVisibleColumns = isCurrentlyVisible
          ? prev.filter((key) => key !== columnKey)
          : [...prev, columnKey];

        // Update column order: if hiding, remove from order; if showing, add to end
        setColumnOrder((prevOrder) => {
          if (isCurrentlyVisible) {
            // Hiding: remove from order
            return prevOrder.filter((key) => key !== columnKey);
          } else {
            // Showing: add to end
            return [...prevOrder, columnKey];
          }
        });

        // Emit column visibility change event
        onColumnVisibilityChange?.(columnKey, !isCurrentlyVisible);

        return newVisibleColumns;
      });
    },
    [onColumnVisibilityChange],
  );

  // Handle filter changes
  const handleFilterChange = useCallback(
    (columnKey: string, filter: ColumnFilter | null) => {
      setFilters((prev) => {
        let newFilters: FilterState;
        if (filter) {
          newFilters = { ...prev, [columnKey]: filter };
        } else {
          newFilters = { ...prev };
          delete newFilters[columnKey];
        }
        // Call the callback with the updated filters
        onFilterChange?.(newFilters);
        return newFilters;
      });
    },
    [onFilterChange],
  );

  const DECIMAL_PRECISION = 3;

  const roundToPrecision = useCallback(
    (num: number, decimals: number = DECIMAL_PRECISION): number => {
      const multiplier = Math.pow(10, decimals);
      return Math.round(num * multiplier) / multiplier;
    },
    [],
  );

  const normalizeValue = useCallback((val: any): string => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') {
      return Array.isArray(val) ? val.join(', ') : JSON.stringify(val);
    }
    return String(val).toLowerCase();
  }, []);

  const isEmptyValue = useCallback((value: any): boolean => {
    return value === null || value === undefined || value === '';
  }, []);

  const isBlankValue = useCallback((value: any): boolean => {
    return (
      value === null ||
      value === undefined ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    );
  }, []);

  const applyFilters = useCallback(
    (data: any[], filters: FilterState) => {
      if (Object.keys(filters).length === 0) return data;

      const columnMap = new Map(columns.map((col) => [col.key, col]));

      return data.filter((row) => {
        return Object.entries(filters).every(([columnKey, filter]) => {
          const column = columnMap.get(columnKey);
          const columnValue = row[columnKey];

          const operatorsWithoutValues = ['is_empty', 'not_empty', 'is_null', 'is_not_null'];
          if (!operatorsWithoutValues.includes(filter.operator)) {
            if (!filter.values || filter.values.length === 0) {
              return true;
            }
          }

          const isEmpty = isEmptyValue(columnValue);

          if (filter.operator === 'is_empty') return isEmpty;
          if (filter.operator === 'not_empty') return !isEmpty;

          if (filter.operator === 'in') {
            const filterValuesLower = filter.values.map((v) => String(v).toLowerCase());
            const hasBlankFilter = filterValuesLower.includes('blank');
            const isBlank = isBlankValue(columnValue);

            if (hasBlankFilter && isBlank) return true;
            if (!hasBlankFilter && isBlank) return false;

            const nonBlankFilterValues = filter.values.filter(
              (v) => String(v).toLowerCase() !== 'blank',
            );

            if (nonBlankFilterValues.length === 0) return !isBlank;
            if (hasBlankFilter && nonBlankFilterValues.length === 0) return false;

            filter = { ...filter, values: nonBlankFilterValues };
          }

          if (column?.customFilter) {
            return column.customFilter(row, filter);
          }

          if (isEmpty) {
            const excludeEmptyOps = [
              'equals',
              'greater_than',
              'less_than',
              'greater_than_or_equal',
              'less_than_or_equal',
              'between',
              'contains',
              'starts_with',
              'ends_with',
              'in',
            ];
            const includeEmptyOps = ['not_equals', 'not_contains', 'not_in'];

            if (excludeEmptyOps.includes(filter.operator)) return false;
            if (includeEmptyOps.includes(filter.operator)) return true;
            return true;
          }

          if (column?.type === 'boolean') {
            const boolValue = Boolean(columnValue);
            const boolFilter = Boolean(filter.values[0]);

            switch (filter.operator) {
              case 'equals':
                return boolValue === boolFilter;
              case 'not_equals':
                return boolValue !== boolFilter;
              default:
                return true;
            }
          }

          if (column?.type === 'number') {
            const numValue = Number(columnValue);
            const numFilter = Number(filter.values[0]);

            if (isNaN(numValue)) return false;
            if (isNaN(numFilter)) return true;

            switch (filter.operator) {
              case 'equals':
                return roundToPrecision(numValue) === roundToPrecision(numFilter);
              case 'not_equals':
                return roundToPrecision(numValue) !== roundToPrecision(numFilter);
              case 'greater_than':
                return numValue > numFilter;
              case 'less_than':
                return numValue < numFilter;
              case 'greater_than_or_equal':
                return numValue >= numFilter;
              case 'less_than_or_equal':
                return numValue <= numFilter;
              case 'between': {
                const numFilter2 = Number(filter.values[1]);
                if (isNaN(numFilter2)) return false;
                return numValue >= numFilter && numValue <= numFilter2;
              }
              default:
                return true;
            }
          }

          const columnValueLower = normalizeValue(columnValue);
          const filterValuesLower = filter.values.map(normalizeValue);

          switch (filter.operator) {
            case 'equals':
              return filterValuesLower.includes(columnValueLower);
            case 'not_equals':
              return !filterValuesLower.includes(columnValueLower);
            case 'contains':
              return filterValuesLower.some((v) => columnValueLower.includes(v));
            case 'not_contains':
              return !filterValuesLower.some((v) => columnValueLower.includes(v));
            case 'starts_with':
              return filterValuesLower.some((v) => columnValueLower.startsWith(v));
            case 'ends_with':
              return filterValuesLower.some((v) => columnValueLower.endsWith(v));
            case 'in':
              if (Array.isArray(columnValue)) {
                return columnValue.some((cv) => filterValuesLower.includes(normalizeValue(cv)));
              }
              return filterValuesLower.includes(columnValueLower);
            case 'not_in':
              if (Array.isArray(columnValue)) {
                return !columnValue.some((cv) => filterValuesLower.includes(normalizeValue(cv)));
              }
              return !filterValuesLower.includes(columnValueLower);
            default:
              return true;
          }
        });
      });
    },
    [columns, roundToPrecision, normalizeValue, isEmptyValue, isBlankValue],
  );

  // Debounced filter application
  const [debouncedFilters, setDebouncedFilters] = useState<FilterState>(filters);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [filters]);

  // Handle sorting
  const handleSort = useCallback(
    (columnKey: string) => {
      const column = columns.find((col) => col.key === columnKey);
      if (!column?.sortable) return;

      setSortState((prev) => {
        let newDirection: SortDirection = 'none';

        if (prev.columnKey === columnKey) {
          // Cycle through: none -> asc -> desc -> none
          if (prev.direction === 'none') newDirection = 'asc';
          else if (prev.direction === 'asc') newDirection = 'desc';
          else newDirection = 'none';
        } else {
          // New column, start with asc
          newDirection = 'asc';
        }

        // Call the onSortChange callback if provided
        if (onSortChange) {
          onSortChange(columnKey, newDirection);
        }

        return {
          columnKey: newDirection === 'none' ? null : columnKey,
          direction: newDirection,
        };
      });
    },
    [columns, onSortChange],
  );

  // Apply filters and sort data
  const filteredAndSortedData = useMemo(() => {
    // First apply filters
    const filteredData = applyFilters(data, debouncedFilters);

    // Then apply sorting
    if (!sortState.columnKey || sortState.direction === 'none') {
      return filteredData;
    }

    const column = columns.find((col) => col.key === sortState.columnKey);
    if (!column) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortState.columnKey!];
      const bValue = b[sortState.columnKey!];

      if (column.customSort) {
        const sortResult = column.customSort(aValue, bValue);
        return sortState.direction === 'asc' ? sortResult : -sortResult;
      }

      // Default sorting based on type
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortState.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();

      if (sortState.direction === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  }, [data, debouncedFilters, sortState, columns, applyFilters]);

  // Handle checkbox selection - toggle selection for the row
  const handleRowSelect = useCallback(
    (row: TableRowData<T>) => {
      // If controlled (selectedRowIds provided), don't update internal state
      // Just calculate new selection and call callback - parent will update selectedRowIds
      const currentSelected = selectedRows;
      const isSelected = currentSelected.has(row.id);

      let newSet: Set<string | number>;
      if (multiSelect) {
        // Multi-select mode: toggle selection
        newSet = new Set(currentSelected);
        if (isSelected) {
          newSet.delete(row.id);
        } else {
          newSet.add(row.id);
        }
      } else {
        // Single-select mode: replace selection with this row
        newSet = new Set();
        if (!isSelected) {
          newSet.add(row.id);
        }
      }

      // Get selected rows data for callback
      const selectedRowsData = filteredAndSortedData.filter((r) => newSet.has(r.id));

      // If not controlled, update internal state
      if (selectedRowIds === undefined) {
        setInternalSelectedRows(newSet);
      }

      // Call onRowSelect with all selected rows
      if (onRowSelect) {
        onRowSelect(selectedRowsData);
      }
    },
    [multiSelect, onRowSelect, filteredAndSortedData, selectedRowIds, selectedRows],
  );

  // Handle select all (only works in multi-select mode)
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (!multiSelect) return;
      if (checked) {
        // If not controlled, update internal state
        if (selectedRowIds === undefined) {
          setInternalSelectedRows(new Set(filteredAndSortedData.map((row) => row.id)));
        }
        // Call onRowSelect with all selected rows
        if (onRowSelect) {
          onRowSelect(filteredAndSortedData);
        }
      } else {
        // If not controlled, update internal state
        if (selectedRowIds === undefined) {
          setInternalSelectedRows(new Set());
        }
        // Call onRowSelect with empty array
        if (onRowSelect) {
          onRowSelect([]);
        }
      }
    },
    [filteredAndSortedData, multiSelect, onRowSelect, selectedRowIds],
  );

  // Check if all rows are selected (only relevant in multi-select mode)
  const isAllSelected = useMemo(() => {
    if (!multiSelect || filteredAndSortedData.length === 0) return false;
    return filteredAndSortedData.every((row) => selectedRows.has(row.id));
  }, [filteredAndSortedData, selectedRows, multiSelect]);

  // Check if some rows are selected (for indeterminate state - only in multi-select mode)
  const isIndeterminate = useMemo(() => {
    if (!multiSelect) return false;
    const selectedCount = filteredAndSortedData.filter((row) => selectedRows.has(row.id)).length;
    return selectedCount > 0 && selectedCount < filteredAndSortedData.length;
  }, [filteredAndSortedData, selectedRows, multiSelect]);

  // Get current highlighted row index
  const highlightedRowIndex = useMemo(() => {
    if (highlightedRowId === null) return null;
    return filteredAndSortedData.findIndex((row) => row.id === highlightedRowId);
  }, [highlightedRowId, filteredAndSortedData]);

  // Reset highlighted row if it's no longer in the filtered data
  useEffect(() => {
    if (highlightedRowId !== null) {
      const exists = filteredAndSortedData.some((row) => row.id === highlightedRowId);
      if (!exists) {
        setHighlightedRowId(null);
      }
    }
  }, [filteredAndSortedData, highlightedRowId]);

  // Handle row highlight (single click)
  const handleRowHighlight = useCallback((row: TableRowData<T>) => {
    setHighlightedRowId(row.id);
    setIsKeyboardNavEnabled(true);
    if (tableContainerRef.current) {
      tableContainerRef.current.focus();
    }
  }, []);

  // Single vs double click via rowId + time interval (click-only, no dblclick)
  const handleRowClickWithDoubleCheck = useCallback(
    (row: TableRowData<T>) => {
      const now = Date.now();
      const last = lastRowClickRef.current;

      const isDoubleClick =
        last !== null && last.rowId === row.id && now - last.time < ROW_SINGLE_CLICK_DELAY_MS;

      if (rowSingleClickTimeoutRef.current !== null) {
        clearTimeout(rowSingleClickTimeoutRef.current);
        rowSingleClickTimeoutRef.current = null;
      }

      if (isDoubleClick) {
        lastRowClickRef.current = null;
        onRowDoubleClick?.(row);
        return;
      }

      lastRowClickRef.current = { rowId: row.id, time: now };
      if (onRowClick) {
        rowSingleClickTimeoutRef.current = setTimeout(() => {
          rowSingleClickTimeoutRef.current = null;
          onRowClick(row);
        }, ROW_SINGLE_CLICK_DELAY_MS);
      }
    },
    [onRowClick, onRowDoubleClick],
  );

  // Handle row navigation (up/down arrows)
  const navigateRow = useCallback(
    (direction: 'up' | 'down') => {
      if (filteredAndSortedData.length === 0) return;

      let newIndex: number;
      if (highlightedRowIndex === null) {
        // If no row is highlighted, start with first or last
        newIndex = direction === 'down' ? 0 : filteredAndSortedData.length - 1;
      } else {
        if (direction === 'down') {
          newIndex = Math.min(highlightedRowIndex + 1, filteredAndSortedData.length - 1);
        } else {
          newIndex = Math.max(highlightedRowIndex - 1, 0);
        }
      }

      const newRow = filteredAndSortedData[newIndex];
      setHighlightedRowId(newRow.id);

      // Scroll to the highlighted row
      if (virtuosoRef.current && typeof virtuosoRef.current.scrollToIndex === 'function') {
        virtuosoRef.current.scrollToIndex({
          index: newIndex,
          align: 'center',
          behavior: 'smooth',
        });
      }
    },
    [highlightedRowIndex, filteredAndSortedData],
  );

  // Handle horizontal scroll (left/right arrows)
  const handleHorizontalScroll = useCallback((direction: 'left' | 'right') => {
    if (!tableWrapperRef.current) return;

    const scrollAmount = 100; // pixels to scroll
    const currentScrollLeft = tableWrapperRef.current.scrollLeft;
    const newScrollLeft =
      direction === 'right' ? currentScrollLeft + scrollAmount : currentScrollLeft - scrollAmount;

    tableWrapperRef.current.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    });
  }, []);

  // Handle keyboard events
  useEffect(() => {
    if (!isKeyboardNavEnabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle keyboard events when table container is focused or has focusable children
      if (!tableContainerRef.current) return;

      // Check if focus is within the table container
      const activeElement = document.activeElement;
      if (!tableContainerRef.current.contains(activeElement) && activeElement !== document.body) {
        return;
      }

      // Don't handle if user is typing in an input/textarea
      if (
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          (activeElement instanceof HTMLElement && activeElement.isContentEditable))
      ) {
        return;
      }

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          navigateRow('up');
          break;
        case 'ArrowDown':
          e.preventDefault();
          navigateRow('down');
          break;
        case 'ArrowLeft':
          e.preventDefault();
          handleHorizontalScroll('left');
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleHorizontalScroll('right');
          break;
        case ' ':
          // Spacebar - toggle selection in multi-select mode
          if (multiSelect && highlightedRowId !== null) {
            e.preventDefault();
            const highlightedRow = filteredAndSortedData.find((row) => row.id === highlightedRowId);
            if (highlightedRow) {
              handleRowSelect(highlightedRow);
            }
          }
          break;
        case 'Enter':
          // Enter - trigger onRowClick
          if (highlightedRowId !== null && onRowClick) {
            e.preventDefault();
            const highlightedRow = filteredAndSortedData.find((row) => row.id === highlightedRowId);
            if (highlightedRow) {
              onRowClick(highlightedRow);
            }
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    isKeyboardNavEnabled,
    navigateRow,
    handleHorizontalScroll,
    multiSelect,
    highlightedRowId,
    filteredAndSortedData,
    handleRowSelect,
    onRowClick,
    onRowDoubleClick,
  ]);

  useEffect(() => {
    if (!isKeyboardNavEnabled) return;

    const handleFocusOut = (e: FocusEvent) => {
      if (
        tableContainerRef.current &&
        e.relatedTarget instanceof Node &&
        !tableContainerRef.current.contains(e.relatedTarget)
      ) {
        setIsKeyboardNavEnabled(false);
      }
    };

    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('focusout', handleFocusOut);
      return () => {
        container.removeEventListener('focusout', handleFocusOut);
      };
    }
  }, [isKeyboardNavEnabled]);

  // Get visible columns in the correct order
  const visibleColumnsConfig = useMemo(() => {
    const visibleCols = columns.filter((col) => visibleColumns.includes(col.key));

    const orderedCols: ColumnConfig[] = [];
    const unorderedCols: ColumnConfig[] = [];

    columnOrder.forEach((key) => {
      const col = visibleCols.find((c) => c.key === key);
      if (col) {
        orderedCols.push(col);
      }
    });

    visibleCols.forEach((col) => {
      if (!columnOrder.includes(col.key)) {
        unorderedCols.push(col);
      }
    });

    return [...orderedCols, ...unorderedCols];
  }, [columns, visibleColumns, columnOrder]);

  // Handle drag start
  const handleDragStart = useCallback(
    (e: React.DragEvent, columnKey: string) => {
      const column = columns.find((col) => col.key === columnKey);
      if (!column || column.draggable === false) {
        e.preventDefault();
        return;
      }

      setDraggedColumn(columnKey);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', columnKey);
      // Add a slight delay to allow drag image to be set
      setTimeout(() => {
        if (e.dataTransfer) {
          e.dataTransfer.effectAllowed = 'move';
        }
      }, 0);
    },
    [columns],
  );

  // Handle drag over
  const handleDragOver = useCallback(
    (e: React.DragEvent, columnKey: string) => {
      if (!draggedColumn || draggedColumn === columnKey) {
        return;
      }

      const column = columns.find((col) => col.key === columnKey);
      if (!column || column.draggable === false) {
        return;
      }

      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';

      // Determine drop position based on mouse position relative to column center
      const rect = e.currentTarget.getBoundingClientRect();
      const mouseX = e.clientX;
      const columnCenter = rect.left + rect.width / 2;
      const position = mouseX < columnCenter ? 'left' : 'right';

      setDropTarget(columnKey);
      setDropPosition(position);
    },
    [draggedColumn, columns],
  );

  // Handle drop
  const handleDrop = useCallback(
    (e: React.DragEvent, targetColumnKey: string) => {
      e.preventDefault();
      e.stopPropagation();

      if (!draggedColumn || draggedColumn === targetColumnKey) {
        setDraggedColumn(null);
        setDropTarget(null);
        setDropPosition(null);
        return;
      }

      isInternalChangeRef.current = true;

      setColumnOrder((prevOrder) => {
        const withoutDragged = prevOrder.filter((key) => key !== draggedColumn);

        const targetIndex = withoutDragged.indexOf(targetColumnKey);

        let newOrder: string[];
        if (targetIndex === -1) {
          newOrder = [...withoutDragged, draggedColumn];
        } else {
          const insertIndex = dropPosition === 'left' ? targetIndex : targetIndex + 1;
          newOrder = [...withoutDragged];
          newOrder.splice(insertIndex, 0, draggedColumn);
        }

        onColumnOrderChange?.(newOrder);

        return newOrder;
      });

      setDraggedColumn(null);
      setDropTarget(null);
      setDropPosition(null);
    },
    [draggedColumn, dropPosition, onColumnOrderChange],
  );

  const handleDragEnd = useCallback(() => {
    setDraggedColumn(null);
    setDropTarget(null);
    setDropPosition(null);
  }, []);

  // Calculate total table width
  const totalTableWidth = useMemo(() => {
    const visibleWidths = visibleColumnsConfig.reduce((sum, col) => sum + columnWidths[col.key], 0);
    const selectionColumnWidth = multiSelect ? 50 : 0;
    const actionsColumnWidth = showActionCell || showColumnPicker ? 50 : 0;
    return visibleWidths + selectionColumnWidth + actionsColumnWidth;
  }, [visibleColumnsConfig, columnWidths, multiSelect, showActionCell, showColumnPicker]);

  // Render header - memoized to prevent re-renders on data changes
  const renderHeader = useCallback(
    () => (
      <HeaderRow>
        {multiSelect && (
          <SelectionHeaderCell width={50} borderedCell={borderedCell}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Checkbox
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
            </div>
          </SelectionHeaderCell>
        )}
        {visibleColumnsConfig.map((column) => {
          // Get or create filter ref for this column
          if (!filterRefs.current[column.key]) {
            filterRefs.current[column.key] = React.createRef<HTMLDivElement>();
          }

          const isDraggable = column.draggable !== false;
          const isDragging = draggedColumn === column.key;
          const isDropTargetCol = dropTarget === column.key;

          return (
            <HeaderCell
              key={column.key}
              width={columnWidths[column.key]}
              isResizing={isResizing?.columnKey === column.key}
              borderedCell={borderedCell}
              isDragging={isDragging}
              isDropTarget={isDropTargetCol}
              draggable={isDraggable}
              dropPosition={isDropTargetCol ? dropPosition : null}
              onDragStart={(e) => handleDragStart(e, column.key)}
              onDragOver={(e) => handleDragOver(e, column.key)}
              onDrop={(e) => handleDrop(e, column.key)}
              onDragEnd={handleDragEnd}
            >
              <SortButton
                onClick={() => column.sortable && handleSort(column.key)}
                disabled={!column.sortable}
                data-sortable={column.sortable}
              >
                {column.label}
                <SortIcon
                  direction={
                    column.sortable && sortState.columnKey === column.key
                      ? sortState.direction
                      : 'none'
                  }
                  data-sortable={column.sortable}
                >
                  <Icon name={ICONS.ArrowDown} width="16" height="16" />
                </SortIcon>
              </SortButton>
              {column.filterable !== false && (
                <ColumnFilterDropdown
                  timezone={timezone}
                  column={column}
                  filter={filters[column.key] || null}
                  onFilterChange={(filter) => handleFilterChange(column.key, filter)}
                  buttonRef={filterRefs.current[column.key]}
                  data={data}
                  direction={visibleColumnsConfig.indexOf(column) === 0 ? 'left' : 'right'}
                />
              )}
              <ResizeHandle
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleResizeStart(column.key, e.clientX);
                }}
              />
            </HeaderCell>
          );
        })}
        {(showActionCell || showColumnPicker) && (
          <ActionsHeaderCell
            width={50}
            borderedCell={borderedCell}
            style={{
              backgroundColor: showActionCell ? undefined : 'transparent',
              borderLeft: showActionCell ? undefined : 'none',
            }}
          >
            {showColumnPicker && (
              <ColumnPicker
                columns={columns}
                visibleColumns={visibleColumns}
                onToggleColumn={handleToggleColumn}
                buttonRef={columnPickerRef}
                open={columnPickerOpen}
                onOpenChange={setColumnPickerOpen}
              />
            )}
          </ActionsHeaderCell>
        )}
      </HeaderRow>
    ),
    [
      multiSelect,
      borderedCell,
      isAllSelected,
      isIndeterminate,
      visibleColumnsConfig,
      showActionCell,
      showColumnPicker,
      columns,
      visibleColumns,
      handleToggleColumn,
      columnPickerOpen,
      handleSelectAll,
      draggedColumn,
      dropTarget,
      columnWidths,
      isResizing?.columnKey,
      dropPosition,
      handleDragEnd,
      sortState.columnKey,
      sortState.direction,
      timezone,
      filters,
      data,
      handleDragStart,
      handleDragOver,
      handleDrop,
      handleSort,
      handleFilterChange,
      handleResizeStart,
    ],
  );

  // Render row
  const renderRow = (index: number) => {
    const row = filteredAndSortedData[index];
    const isRowSelected = selectedRows.has(row.id);
    const isRowHighlighted = highlightedRowId === row.id;

    return (
      <>
        {multiSelect && (
          <SelectionCell
            width={50}
            borderedCell={borderedCell}
            data-row-selected={isRowSelected ? 'true' : 'false'}
            data-highlight-selected={highlightSelectedRow ? 'true' : 'false'}
            data-row-highlighted={isRowHighlighted ? 'true' : 'false'}
            onClick={(e) => {
              // Only handle click if not clicking on checkbox or its container
              const target = e.target as HTMLElement;
              if (
                target.closest('input[type="checkbox"]') ||
                target.closest('[data-checkbox-container]')
              ) {
                return;
              }
              e.stopPropagation();
              handleRowHighlight(row);
              handleRowClickWithDoubleCheck(row);
            }}
          >
            <div
              data-checkbox-container
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <Checkbox
                checked={isRowSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  handleRowSelect(row);
                }}
              />
            </div>
          </SelectionCell>
        )}
        {visibleColumnsConfig.map((column) => {
          const cellValue = row[column.key];
          const tooltipContent = column.cellTooltip?.(row, cellValue);
          const renderedContent = column.renderCell ? column.renderCell(row, cellValue) : cellValue;

          return (
            <DataCell
              key={column.key}
              width={columnWidths[column.key]}
              isClickable={!!onRowClick}
              borderedCell={borderedCell}
              align={column.align}
              data-row-selected={isRowSelected ? 'true' : 'false'}
              data-highlight-selected={highlightSelectedRow ? 'true' : 'false'}
              data-row-highlighted={isRowHighlighted ? 'true' : 'false'}
              onClick={() => {
                handleRowHighlight(row);
                handleRowClickWithDoubleCheck(row);
              }}
            >
              {tooltipContent ? (
                <Tooltip
                  version="v2"
                  direction={column.cellTooltipDirection || 'right'}
                  label={String(tooltipContent)}
                >
                  <CellContent width={columnWidths[column.key]}>{renderedContent}</CellContent>
                </Tooltip>
              ) : (
                <CellContent width={columnWidths[column.key]}>{renderedContent}</CellContent>
              )}
            </DataCell>
          );
        })}
        {(showActionCell || showColumnPicker) && (
          <ActionsCell
            width={50}
            borderedCell={borderedCell}
            data-row-highlighted={isRowHighlighted ? 'true' : 'false'}
            style={{
              backgroundColor: showActionCell ? undefined : 'transparent',
              borderLeft: showActionCell ? undefined : 'none',
            }}
            onClick={() => {
              handleRowHighlight(row);
              handleRowClickWithDoubleCheck(row);
            }}
          >
            {showActionCell && renderActions?.(row)}
          </ActionsCell>
        )}
      </>
    );
  };

  // Memoize the table components to prevent re-renders
  const tableComponents = useMemo(
    () => ({
      Scroller: React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
        (props, ref) => {
          // Capture the wrapper ref for horizontal scrolling
          const handleRef = (element: HTMLDivElement | null) => {
            tableWrapperRef.current = element;
            if (ref) {
              if (typeof ref === 'function') {
                ref(element);
              } else if (typeof ref === 'object' && ref !== null) {
                (ref as React.MutableRefObject<HTMLDivElement | null>).current = element;
              }
            }
          };
          return <VirtuosoScroller {...props} ref={handleRef} />;
        },
      ) as React.ComponentType<React.HTMLAttributes<HTMLDivElement>>,
      Table: (props: any) => (
        <VirtuosoTable {...props} style={{ ...props.style, minWidth: totalTableWidth }} />
      ),
      TableHead: VirtuosoTableHead,
      TableBody: VirtuosoTableBody,
      EmptyPlaceholder: () =>
        isDataLoading ? null : (
          <tbody>
            <tr>
              <td
                colSpan={
                  visibleColumnsConfig.length +
                  (multiSelect ? 1 : 0) +
                  (showActionCell || showColumnPicker ? 1 : 0)
                }
                style={{ textAlign: 'center' }}
              >
                <EmptyState>No data available</EmptyState>
              </td>
            </tr>
          </tbody>
        ),
    }),
    [
      totalTableWidth,
      visibleColumnsConfig.length,
      multiSelect,
      showActionCell,
      showColumnPicker,
      isDataLoading,
    ],
  );

  return (
    <TableContainer
      ref={tableContainerRef}
      className={className}
      style={style}
      tabIndex={0}
      onFocus={(e) => {
        // If clicking on the container itself, focus it for keyboard navigation
        if (e.target === tableContainerRef.current) {
          tableContainerRef.current?.focus();
        }
      }}
    >
      {title && <DataTableHeader>{title}</DataTableHeader>}
      {isDataLoading && (
        <LoadingOverlay hasTitle={!!title}>
          Loading data <Icon name={ICONS.Loader} width="24" height="24" />
        </LoadingOverlay>
      )}
      <TableVirtuoso
        ref={virtuosoRef}
        style={{
          borderRadius: 0,
          border: 'none',
          width: '100%',
          height: '100%',
        }}
        totalCount={isDataLoading ? 0 : filteredAndSortedData.length}
        components={tableComponents}
        fixedHeaderContent={renderHeader}
        itemContent={renderRow}
        fixedItemHeight={rowHeight}
      />
    </TableContainer>
  );
};

export default DataTable;
